import { useEffect, useState } from "react";
import { Navigate, useSearchParams } from "react-router-dom";
import { Show } from "@clerk/react";
import { ShieldAlert } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useAdminApi } from "@/hooks/useAdminApi";
import { money } from "@/lib/api";
import { OrdersTab } from "@/components/admin/OrdersTab";
import { GamesTab } from "@/components/admin/GamesTab";
import { WalletsTab } from "@/components/admin/WalletsTab";
import { UsersTab } from "@/components/admin/UsersTab";
import { ChatsTab } from "@/components/admin/ChatsTab";
import { BlogTab } from "@/components/admin/BlogTab";
import { BansTab } from "@/components/admin/BansTab";
import { PartnersTab } from "@/components/admin/PartnersTab";
import { RemindersTab } from "@/components/admin/RemindersTab";

const Stat = ({ label, value, testid, accent }) => (
  <div className="rounded-lg border border-white/10 bg-[#0F1117] p-4 sm:p-5">
    <p className="label-eyebrow text-gray-500">{label}</p>
    <p data-testid={testid} className={`font-mono text-2xl sm:text-3xl font-bold mt-2 ${accent || ""}`}>{value}</p>
  </div>
);

const Dashboard = () => {
  const call = useAdminApi();
  const [role, setRole] = useState(null);
  const [stats, setStats] = useState(null);
  const chatId = useSearchParams()[0].get("chat");
  const refresh = () => call("get", "/admin/stats").then(setStats).catch(() => {});

  useEffect(() => { call("get", "/me").then((m) => { setRole(m.role); if (m.role === "admin") refresh(); }).catch(() => setRole("error")); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (!role) return <div className="h-[60vh]" />;
  if (role !== "admin") return (
    <div data-testid="admin-access-denied" className="text-center py-32 space-y-3">
      <ShieldAlert className="mx-auto text-amber-400" size={40} />
      <p className="font-display text-xl">No admin access</p>
      <p className="text-gray-400 text-sm">This account isn't a store admin.</p>
    </div>
  );

  const triggerCls = "shrink-0 data-[state=active]:bg-emerald-500 data-[state=active]:text-[#08090C]";
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-8 pt-6 sm:pt-10 space-y-6 sm:space-y-8">
      <div>
        <p className="label-eyebrow text-emerald-400">Admin</p>
        <h1 className="font-display font-bold tracking-tight text-2xl sm:text-4xl mt-2">Store control</h1>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4">
        <Stat label="Pending orders" value={stats?.pending ?? "—"} testid="stat-pending" accent="text-amber-400" />
        <Stat label="Delivered" value={stats?.delivered ?? "—"} testid="stat-delivered" />
        <Stat label="Revenue (USDT)" value={stats ? money(stats.revenue) : "—"} testid="stat-revenue" accent="text-emerald-400" />
        <Stat label="Hidden (need price)" value={stats?.hidden ?? "—"} testid="stat-hidden" />
      </div>
      <Tabs defaultValue={chatId ? "chats" : "orders"}>
        <TabsList data-testid="admin-tabs-list" className="bg-[#0F1117] border border-white/10 h-auto w-full sm:w-auto flex flex-nowrap sm:flex-wrap overflow-x-auto no-scrollbar justify-start pr-10 sm:pr-1 [mask-image:linear-gradient(to_right,black_92%,transparent)] sm:[mask-image:none]">
          <TabsTrigger value="orders" data-testid="admin-tab-orders" className={triggerCls}>Orders</TabsTrigger>
          <TabsTrigger value="chats" data-testid="admin-tab-chats" className={triggerCls}>Chats{stats?.unread_chats ? <span data-testid="admin-unread-chats" className="ml-2 text-[10px] font-bold bg-red-500 text-white rounded-full px-1.5">{stats.unread_chats}</span> : null}</TabsTrigger>
          <TabsTrigger value="games" data-testid="admin-tab-games" className={triggerCls}>Games</TabsTrigger>
          <TabsTrigger value="boxes" data-testid="admin-tab-boxes" className={triggerCls}>Mystery boxes</TabsTrigger>
          <TabsTrigger value="blog" data-testid="admin-tab-blog" className={triggerCls}>Blog</TabsTrigger>
          <TabsTrigger value="bans" data-testid="admin-tab-bans" className={triggerCls}>Bans</TabsTrigger>
          <TabsTrigger value="reminders" data-testid="admin-tab-reminders" className={triggerCls}>Unpaid</TabsTrigger>
          <TabsTrigger value="partners" data-testid="admin-tab-partners" className={triggerCls}>Partner API</TabsTrigger>
          <TabsTrigger value="wallets" data-testid="admin-tab-wallets" className={triggerCls}>Wallets</TabsTrigger>
          <TabsTrigger value="users" data-testid="admin-tab-users" className={triggerCls}>Users</TabsTrigger>
        </TabsList>
        <TabsContent value="orders" className="mt-6"><OrdersTab onChange={refresh} /></TabsContent>
        <TabsContent value="chats" className="mt-6"><ChatsTab onChange={refresh} initialId={chatId} /></TabsContent>
        <TabsContent value="games" className="mt-6"><GamesTab onChange={refresh} /></TabsContent>
        <TabsContent value="boxes" className="mt-6"><GamesTab kind="box" onChange={refresh} /></TabsContent>
        <TabsContent value="blog" className="mt-6"><BlogTab /></TabsContent>
        <TabsContent value="bans" className="mt-6"><BansTab /></TabsContent>
        <TabsContent value="reminders" className="mt-6"><RemindersTab /></TabsContent>
        <TabsContent value="partners" className="mt-6"><PartnersTab /></TabsContent>
        <TabsContent value="wallets" className="mt-6"><WalletsTab /></TabsContent>
        <TabsContent value="users" className="mt-6"><UsersTab /></TabsContent>
      </Tabs>
    </div>
  );
};

export default function Admin() {
  return (
    <>
      <Show when="signed-in"><Dashboard /></Show>
      <Show when="signed-out"><Navigate to="/sign-in" replace /></Show>
    </>
  );
}
